import type { RequestOptions } from '@frontegg/rest-api';
import type { LDClient } from 'launchdarkly-js-client-sdk';
import { isDebugMode } from '../debug/debugUser';
import { MaggieHttp } from '../services/MaggieHttp';
import { PermissionService } from '../services/PermissionService';
import { authenticatedUserState, earlyAccessTokenState } from '../services/state/AuthState';
import { FeatureFlags } from './feature-flags';
import { getForesightStore } from './jotai-store';
import { decodeJwtPayload, isTokenExpired } from './jwt';
import { primeBootReads, primeFormulaFields, primeLandingData } from './landingPrefetch';
import { getLdFlag, identifyLdUser, setLdClient } from './ld-client';

// Frontegg's silent refresh holds the new access token about a second and a half before its user object
// arrives: it loads me, tenants and entitlements first, and only then reports the user. The boot's requests
// need only the token, so they start from it. Permissions, and everything they gate, still wait for the user.

type Store = ReturnType<typeof getForesightStore>;

let bootReadsStarted = false;

/**
 * Frontegg calls this before each of its own requests (ContextOptions.beforeRequestInterceptor). The first
 * after the silent refresh, users/v2/me, carries the new token; the boot starts from it outside Frontegg's
 * call stack. The request goes out as it was: the `url` that Frontegg's type asks back is the same one,
 * and fetch ignores it in the options.
 */
export function noteFronteggRequest(options: Omit<RequestOptions, 'url'>, url: string): RequestOptions {
  const token = options.headers?.Authorization?.replace(/^Bearer /, '');
  if (token) {
    queueMicrotask(() => startEarlyBoot(token));
  }
  return { ...options, url };
}

/**
 * Starts the boot's requests from Frontegg's access token, before its user object arrives. Does nothing
 * once the user has loaded, in debug mode, on a token the expired-token redirect would refuse, or for a
 * portal-only user, who never reaches the app.
 */
export function startEarlyBoot(token: string, store: Store = getForesightStore()): void {
  try {
    if (isDebugMode() || store.get(authenticatedUserState) || isTokenExpired(token)) {
      return;
    }
    const access = PermissionService.forAccessToken(token);
    if (access.isPortalOnly()) {
      return;
    }
    const firstToken = !bootReadsStarted;
    bootReadsStarted = true;
    // The clients first: publishing the token wakes the early LaunchDarkly identify, which makes requests.
    if (firstToken) {
      MaggieHttp.initForSession(store);
    }
    store.set(earlyAccessTokenState, token);
    if (firstToken) {
      primeBootReads(access, store);
    }
  } catch {
    // a head start only; the boot makes the same requests once the user loads
  }
}

/**
 * Identifies the user to LaunchDarkly by the token's email claim, then starts the reads that wait on its
 * flags (views read `useNewViewsP`, formula fields their own flag). The boot's identify reuses this one.
 */
export function identifyLaunchDarklyEarly(
  ldClient: LDClient,
  token: string,
  store: Store = getForesightStore()
): void {
  const email = decodeJwtPayload(token)?.email;
  if (typeof email !== 'string' || !email) {
    return;
  }
  identifyLdUser(ldClient, email)
    .then(() => {
      setLdClient(ldClient);
      primeLandingData(window.location.pathname, store, PermissionService.forAccessToken(token));
      primeFormulaFields(getLdFlag(FeatureFlags.showFormulaFields, false), store);
    })
    .catch(() => undefined);
}

export function __TEST_ONLY__resetEarlyBoot() {
  bootReadsStarted = false;
}
